import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../config/axios';
import toast from 'react-hot-toast';
import { Plus, Eye, Edit, Trash2, Copy, BarChart3, LayoutGrid, ClipboardList, Target, Search, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const SURVEY_TYPE_FILTERS = [
  { key: 'all', label: 'Todas', icon: LayoutGrid },
  { key: 'normal', label: 'Bienestar Social', icon: ClipboardList },
  { key: 'performance', label: 'Desempeño', icon: Target }
];

const isPerformanceSurvey = (survey) => survey.performance === true || survey.performance === 'true';

const Dashboard = () => {
  const { user } = useAuth();
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [surveyTypeFilter, setSurveyTypeFilter] = useState('all');
  const [surveySearch, setSurveySearch] = useState('');
  const filterOwner = user?.id || user?._id || user?.username || 'default';
  const filterStorageKey = `gp-forms:dashboard:survey-type:${encodeURIComponent(filterOwner)}`;

  useEffect(() => {
    fetchSurveys();
  }, []);

  useEffect(() => {
    try {
      const storedFilter = localStorage.getItem(filterStorageKey);
      const isValidFilter = SURVEY_TYPE_FILTERS.some(option => option.key === storedFilter);
      setSurveyTypeFilter(isValidFilter ? storedFilter : 'all');
    } catch (error) {
      setSurveyTypeFilter('all');
    }
  }, [filterStorageKey]);

  const fetchSurveys = async () => {
    try {
      const response = await api.get('/api/forms');
      let surveys = response.data.map(s => ({ ...s, id: s._id || s.id }));
      // Obtener analytics para respuestas
      const analyticsRes = await api.get('/api/analytics/surveys');
      const analyticsMap = {};
      analyticsRes.data.forEach(a => { analyticsMap[a.id] = a.responseCount; });
      surveys = surveys.map(s => ({ ...s, responseCount: analyticsMap[s.id] ?? 0 }));
      setSurveys(surveys);
    } catch (error) {
      toast.error('No se pudieron obtener las encuestas');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this survey?')) {
      try {
        await api.delete(`/api/forms/${id}`);
        toast.success('Encuesta eliminada exitosamente');
        fetchSurveys();
      } catch (error) {
        const msg = error.response?.data?.message || error.message || 'Failed to delete survey';
        toast.error(msg);
      }
    }
  };

  const handleToggleStatus = async (survey) => {
    try {
      await api.put(`/api/forms/${survey.id}`, {
        ...survey,
        isActive: !survey.isActive
      });
      toast.success(`Encuesta ${survey.isActive ? 'desactivada' : 'activada'} exitosamente`);
      fetchSurveys();
    } catch (error) {
      toast.error('No se pudo actualizar el estado de la encuesta');
    }
  };

  const copySurveyLink = (id) => {
    const link = `${window.location.origin}/form/${id}`;
    navigator.clipboard.writeText(link);
    toast.success('¡Enlace de encuesta copiado al portapapeles!');
  };

  const handleSurveyTypeFilterChange = (filter) => {
    setSurveyTypeFilter(filter);
    try {
      localStorage.setItem(filterStorageKey, filter);
    } catch (error) {
      // El filtro sigue funcionando durante la sesión aunque el navegador bloquee el almacenamiento.
    }
  };

  // Agrega estilos para truncar a 2 líneas con puntos suspensivos
  const twoLineTruncate = {
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
  };

  if (loading) {
    return <div className="loading">Cargando encuestas...</div>;
  }

  // Ordenar encuestas por fecha de creación descendente (más recientes primero)
  const sortedSurveys = [...surveys].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  const normalizedSurveySearch = surveySearch.trim().toLocaleLowerCase('es');
  const searchedSurveys = sortedSurveys.filter(survey => {
    if (!normalizedSurveySearch) return true;
    return [survey.title, survey.description]
      .filter(Boolean)
      .some(value => String(value).toLocaleLowerCase('es').includes(normalizedSurveySearch));
  });
  const surveyTypeCounts = searchedSurveys.reduce((counts, survey) => {
    if (isPerformanceSurvey(survey)) counts.performance += 1;
    else counts.normal += 1;
    return counts;
  }, { all: searchedSurveys.length, normal: 0, performance: 0 });
  const filteredSurveys = searchedSurveys.filter(survey => {
    if (surveyTypeFilter === 'performance') return isPerformanceSurvey(survey);
    if (surveyTypeFilter === 'normal') return !isPerformanceSurvey(survey);
    return true;
  });

  return (
    <div className="dashboard-bg">
      <div className="dashboard-header dashboard-surveys-header">
        <h1 className="dashboard-title">Encuestas</h1>
        <Link to="/builder" className="dashboard-create-btn-unique">
          <Plus size={22} className="dashboard-create-btn-icon" />
          Crear Nueva Encuesta
        </Link>
      </div>
      <div className="dashboard-survey-filter-toolbar">
        <div className="dashboard-survey-search">
          <Search size={18} aria-hidden="true" />
          <input
            type="search"
            value={surveySearch}
            onChange={(event) => setSurveySearch(event.target.value)}
            placeholder="Buscar encuestas"
            aria-label="Buscar encuestas por título o descripción"
          />
          {surveySearch && (
            <button
              type="button"
              className="dashboard-survey-search-clear"
              onClick={() => setSurveySearch('')}
              aria-label="Limpiar búsqueda"
              title="Limpiar búsqueda"
            >
              <X size={16} strokeWidth={2.2} aria-hidden="true" />
            </button>
          )}
        </div>
        <div className="dashboard-survey-type-filter" role="group" aria-label="Filtrar encuestas por tipo">
          {SURVEY_TYPE_FILTERS.map(option => {
            const Icon = option.icon;
            const isActive = surveyTypeFilter === option.key;
            return (
              <button
                key={option.key}
                type="button"
                className={`dashboard-survey-type-option ${isActive ? 'is-active' : ''}`}
                aria-pressed={isActive}
                onClick={() => handleSurveyTypeFilterChange(option.key)}
              >
                <Icon size={17} aria-hidden="true" />
                <span>{option.label}</span>
                <span className="dashboard-survey-type-count">{surveyTypeCounts[option.key]}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div className="surveys-grid">
        {filteredSurveys.map((survey) => (
          <div key={survey.id} className="survey-card">
            {(() => {
              const maxReached = survey.maxResponses && survey.responseCount >= Number(survey.maxResponses);
              const now = new Date();
              const start = survey.startDate ? new Date(survey.startDate) : null;
              const end = survey.endDate ? new Date(survey.endDate) : null;
              const hasScheduleOrMax = !!(survey.startDate || survey.endDate || maxReached);
              const outOfWindow = maxReached || (start && now < start) || (end && now > end);
              const effectiveActive = hasScheduleOrMax ? !outOfWindow : !!survey.isActive;
              return (
                <span className={"survey-badge " + (effectiveActive ? 'active' : 'inactive')}>
                  {effectiveActive ? 'Activo' : 'Inactivo'}
                </span>
              );
            })()}
            <div className="survey-card-header">
              <h3 className="survey-title" style={{ ...twoLineTruncate }}>{survey.title}</h3>
            </div>
            <p className="survey-description" style={{ ...twoLineTruncate }}>{survey.description || 'No hay descripción'}</p>
            <div className="survey-actions-row">
              <button onClick={() => copySurveyLink(survey.id)} className="survey-action-btn" title="Copiar enlace de la encuesta">
                <Copy size={16} />
              </button>
              <Link to={`/form/${survey.id}`} className="survey-action-btn" title="Vista previa">
                <Eye size={16} />
              </Link>
              <Link to={`/builder?edit=${survey.id}`} className="survey-action-btn" title="Editar encuesta">
                <Edit size={16} />
              </Link>
            </div>
            <div className="survey-actions-row">
              <Link to={`/analytics?form=${survey.id}`} className="survey-action-btn" title="Ver analítica">
                <BarChart3 size={16} />
              </Link>
              <button onClick={() => handleToggleStatus(survey)} className={"survey-action-btn " + (survey.isActive ? 'deactivate' : 'activate')} title={survey.isActive ? 'Desactivar encuesta' : 'Activar encuesta'} disabled={!!(survey.startDate || survey.endDate || (survey.maxResponses && survey.responseCount >= Number(survey.maxResponses)))}>
                {survey.isActive ? 'Desactivar' : 'Activar'}
              </button>
              <button onClick={() => handleDelete(survey.id)} className="survey-action-btn danger" title="Eliminar encuesta">
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
        {filteredSurveys.length === 0 && (
          <div className="dashboard-survey-filter-empty">
            <ClipboardList size={28} aria-hidden="true" />
            <strong>{normalizedSurveySearch ? 'No se encontraron encuestas' : 'No hay encuestas en esta categoría'}</strong>
            <span>{normalizedSurveySearch ? 'Prueba con otro término o cambia el filtro.' : 'Selecciona otro tipo de encuesta para continuar.'}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
